import { useState, useEffect } from 'react';
import { Contractor } from '../types/contractor';
import { BsStar, BsStarFill, BsSearch } from 'react-icons/bs';
import { useKeycloak } from '@react-keycloak/web';
import { API_URL } from '../config/api';

interface User {
  id: string;
  email: string | null;
  username: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_email_verified: boolean;
  created_at: string | null;
  updated_at: string | null;
  active_to: string | null;
}

interface PaginatedResponse<T> {
  total: number;
  page: number;
  size: number;
  results: T[];
}

const getAvailabilityColor = (availability: Contractor['availability']) => {
  switch (availability) {
    case 'available':
      return 'bg-green-100 text-green-800';
    case 'on_project':
      return 'bg-primary/10 text-primary';
    case 'vacation':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const StarRating = ({ rating }: { rating: number }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - Math.ceil(rating);

  return (
    <div className="flex items-center gap-0.5">
      {[...Array(fullStars)].map((_, i) => (
        <BsStarFill key={`full-${i}`} className="w-4 h-4 text-yellow-400" />
      ))}
      {hasHalfStar && <BsStarFill className="w-4 h-4 text-yellow-400" />}
      {[...Array(emptyStars)].map((_, i) => (
        <BsStar key={`empty-${i}`} className="w-4 h-4 text-gray-300" />
      ))}
      <span className="ml-1 text-sm text-gray-600">{rating.toFixed(1)}</span>
    </div>
  );
};

export const Marketplace = () => {
  const { keycloak } = useKeycloak();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<Contractor['availability'] | 'all'>('all');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!keycloak.token) return;
      
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_URL}/users/?page=${page}&size=20${searchTerm ? `&search=${searchTerm}` : ''}`,
          {
            headers: {
              'content-type': 'application/json',
              'authorization': 'Bearer ' + keycloak.token
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        
        const data: PaginatedResponse<User> = await response.json();
        setUsers(data.results);
        setTotalPages(Math.ceil(data.total / data.size));
      } catch (err) {
        setError('Failed to fetch users. Please try again later.');
        console.error('Error fetching users:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page, searchTerm, keycloak.token]);

  // Get unique skills from all contractors
  const allSkills = ['Electrical Systems', 'HVAC', 'Network Infrastructure', 'Security Systems', 'Power Distribution'];

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold mb-6">Contractor Marketplace</h1>
        
        {/* Search and Filters */}
        <div className="space-y-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1); // Reset to first page on new search
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <BsSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Skills</label>
              <select
                multiple
                value={selectedSkills}
                onChange={(e) => setSelectedSkills(Array.from(e.target.selectedOptions, option => option.value))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {allSkills.map(skill => (
                  <option key={skill} value={skill}>{skill}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Availability</label>
              <select
                value={selectedAvailability}
                onChange={(e) => setSelectedAvailability(e.target.value as Contractor['availability'] | 'all')}
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="available">Available</option>
                <option value="on_project">On Project</option>
                <option value="vacation">On Vacation</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading contractors...</p>
        </div>
      )}

      {error && (
        <div className="text-center py-12">
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Contractors Grid */}
      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map(user => (
            <div key={user.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <img
                    src={`https://randomuser.me/api/portraits/${Math.random() > 0.5 ? 'men' : 'women'}/${Math.floor(Math.random() * 10) + 1}.jpg`}
                    alt={`${user.first_name} ${user.last_name}`}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">
                      {user.first_name} {user.last_name}
                    </h3>
                    <StarRating rating={4.5} />
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAvailabilityColor(user.is_active ? 'available' : 'vacation')}`}>
                        {user.is_active ? 'Available' : 'Not Available'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-sm font-medium text-gray-700">Contact:</div>
                  <div className="mt-1 text-sm text-gray-600">
                    {user.email && <div>{user.email}</div>}
                    <div>Username: {user.username}</div>
                  </div>
                </div>

                <button className="w-full mt-4 bg-primary text-white py-2 rounded-lg hover:bg-primary/90 transition-colors">
                  View Profile
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && totalPages > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 border rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {!loading && !error && users.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No contractors found matching your criteria.</p>
        </div>
      )}
    </div>
  );
}; 